import { ChangeDetectorRef, Component, Input, OnInit } from '@angular/core';
import { FormGroup, FormBuilder } from '@angular/forms';
import { Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { Subscription } from 'rxjs';
import {
  IQuestionnaireChoice,
  IQuestionnaireQuestion,
} from 'src/app/interfaces/Questionnaire/IQuestionnaire';
import { EpisodeOfCareService } from 'src/app/services/episodeOfCare.service';
import { PermissionService } from 'src/app/services/permission.service';

@Component({
  selector: 'app-speech-therapy-bdi-questions',
  templateUrl: './speech-therapy-bdi-questions.component.html',
  styleUrl: './speech-therapy-bdi-questions.component.css',
  standalone: false,
})
export class SpeechTherapyBDIQuestionsComponent implements OnInit {
  @Input() elements: IQuestionnaireQuestion[] = [];
  @Input() formGroup!: FormGroup;
  @Input() readonlyMode = false;

  private sub?: Subscription;

  private readonly totalControlName = 'question108';

  constructor(
    private fb: FormBuilder,
    private readonly eocService: EpisodeOfCareService,
    private readonly router: Router,
    private readonly cdr: ChangeDetectorRef,
    private readonly translate: TranslateService,
    private readonly permissionService: PermissionService,
  ) {}

  isInpatientView(): boolean {
    return (
      !(
        this.eocService.currentEocId() === null ||
        this.eocService.currentEocId() === ''
      ) && this.router.url.includes('inPatientView')
    );
  }

  isEOC() {
    return (
      !(
        this.eocService.currentEocId() === null ||
        this.eocService.currentEocId() === ''
      ) && this.router.url.includes('episodeOfCare')
    );
  }

  hasPermission(permission: string[]): boolean {
    return this.permissionService.can(permission);
  }

  ngOnInit() {
    const totalCtrl = this.formGroup.get(this.totalControlName);
    if (totalCtrl) {
      totalCtrl.disable({ emitEvent: false });
    }

    // Recalculate on any changes
    this.sub = this.formGroup.valueChanges.subscribe(() => {
      this.recalculateTotal();
    });

    // Initial
    this.recalculateTotal();
  }

  private recalculateTotal(): void {
    const totalCtrl = this.formGroup.get(this.totalControlName);
    if (!totalCtrl) return;

    let total = 0;

    for (const el of this.elements ?? []) {
      if (!el.name) continue;
      if (el.name === this.totalControlName) continue;

      const selectedValue = this.formGroup.get(el.name)?.value;
      if (selectedValue == null || selectedValue === '') continue;

      const choice = (el.choices ?? []).find(
        (c: IQuestionnaireChoice) => c.value === selectedValue,
      );

      const selectedText = (choice?.text ?? '').trim();
      total += this.scoreBdiAnswer(selectedText);
    }

    totalCtrl.setValue(String(total), { emitEvent: false });
    totalCtrl.disable({ emitEvent: false });
    this.cdr.detectChanges();
  }

  private scoreBdiAnswer(selectedText: string): number {
    if (!selectedText) return 0;

    const m = selectedText.match(/^(\d+)\s*[a-zA-Zα-ωΑ-Ω]?\s*\./);

    return m ? Number(m[1]) : 0;
  }

  setNotMentioned(fieldName: string): void {
    this.formGroup
      .get(fieldName)
      ?.setValue(this.translate.instant('Not mentioned'));
    this.cdr.detectChanges(); // Ensure UI updates immediately
  }

  onRadioClick(controlName: string, value: any): void {
    if (this.readonlyMode) return;
    const control = this.formGroup.get(controlName);
    if (!control) return;

    if (control.value === value) {
      control.setValue(null); // unselect
    }
  }

  get totalScore(): number {
    const v = this.formGroup?.get(this.totalControlName)?.value;
    const n = Number(v);
    return Number.isFinite(n) ? n : 0;
  }
}
